'use client';

import { useEffect, useRef } from 'react';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { ArrivalOrderModal } from '@/app/(main)/(pages)/_components/schedules/arrival-order/arrival-order-modal.component';
import { SchedulesSearchSlot, SearchPartnerData } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import { useSlotFilters } from '@/app/(main)/(pages)/busca/[slug]/_hooks/use-slot-filters.hook';
import { EntityTypeEnum } from '@/types/enums/entity-type.enum';
import useDeviceType from '@/hooks/use-device-type.hook';
import { partnerBranchDisplayName } from '@/utils/formatters/branch';
import { professionalDisplayName } from '@/utils/formatters/professional-name';
import { sumProcedurePrices } from '@/utils/procedures/procedures.util';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

interface SearchCardArrivalOrderModalProps {
  scheduleId: string | undefined;
  schedule: SearchPartnerData;
  busySlotIds: ReadonlySet<string>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectSlot: (slot: SchedulesSearchSlot) => void;
}

export function SearchCardArrivalOrderModal({
  scheduleId,
  schedule,
  busySlotIds,
  open,
  onOpenChange,
  onSelectSlot,
}: SearchCardArrivalOrderModalProps) {
  const deviceType = useDeviceType();
  const analytics = getAnalyticsPlugin();
  const wasOpen = useRef(open);
  const slotFilters = useSlotFilters();

  const { professional } = schedule;
  const entityName = professional?.id
    ? professionalDisplayName(professional)
    : partnerBranchDisplayName(schedule.partner);
  const entityAvatar = professional?.id ? professional.avatar : schedule.partner.avatar;
  const entityType = professional?.id ? EntityTypeEnum.PROFESSIONAL : EntityTypeEnum.LABORATORY_COLLECT_POINT;
  const price = sumProcedurePrices(schedule.procedures);

  useEffect(() => {
    if (open && !wasOpen.current) {
      analytics.track(AnalyticsEnum.VIEW_ARRIVAL_ORDER_MODAL, {
        partner_id: schedule.partner.id,
        device_type: deviceType,
      });
    }
    wasOpen.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    analytics.track(AnalyticsEnum.CLOSE_ARRIVAL_ORDER_MODAL, { partner_id: schedule.partner.id });
    onOpenChange(false);
  };

  return (
    <ArrivalOrderModal
      scheduleId={scheduleId}
      entityName={entityName}
      entityAvatar={entityAvatar}
      entityType={entityType}
      partnerId={schedule.partner.id}
      price={price}
      busySlotIds={busySlotIds}
      open={open}
      slotFilters={slotFilters}
      dataTestId={DataTestIdEnum.SEARCH_CARD_ARRIVAL_ORDER_MODAL}
      closeDataTestId={DataTestIdEnum.SEARCH_CARD_ARRIVAL_ORDER_MODAL_CLOSE}
      onOpenChange={onOpenChange}
      onClose={handleClose}
      onSelectSlot={onSelectSlot}
    />
  );
}
